import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';

// Parametros de la busqueda por vehiculo (se limpian al cambiar de categoria)
const PARAMS_BUSQUEDA = ['buscar', 'marca_auto', 'modelo_auto', 'bateria', 'eq1'];

/** Normaliza un BCI para comparar: "bci 24-F" -> "24F", "Grupo 65" -> "65". */
const normBci = (s: string | null | undefined) =>
  (s || '')
    .toUpperCase()
    .replace(/[^A-Z0-9/]/g, '')
    .replace(/^(BCI|GRUPO|GR)(?=\d)/, '');

const etiquetaBci = (bci: string) => (/^BCI/i.test(bci.trim()) ? bci.trim() : `BCI ${bci.trim()}`);

type SugerenciaBci = { bci: string; cantidad: number; via?: string };
type Equivalencia = { codigo: string; bci: string; cantidad: number };

function DescripcionExpandible({ texto }: { texto: string }) {
  const [expandida, setExpandida] = useState(false);
  if (!texto) return null;
  const corta = texto.length > 60;
  return (
    <div className="mb-2">
      <p className={`text-gray-600 text-xs ${!expandida && corta ? 'line-clamp-2' : ''}`}>{texto}</p>
      {corta && (
        <button onClick={e => { e.preventDefault(); setExpandida(!expandida); }}
          className="mt-1 block animate-pulse">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E30613" strokeWidth="2.5" style={{transform: expandida ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s'}}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * Casilla "Modelos BCI" con predictor.
 * - Sugiere los BCI que existen en los productos cargados (con cuantas baterias tiene cada uno).
 * - Si el cliente escribe un codigo equivalente (N50Z, 78DT...), sugiere su BCI.
 */
function BuscadorBci({ productos, activo, referencia, onElegir, onQuitar }: {
  productos: any[];
  activo: string | null;
  referencia: string | null;
  onElegir: (bci: string, via?: string) => void;
  onQuitar: () => void;
}) {
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(-1);
  const [equivalencias, setEquivalencias] = useState<Equivalencia[]>([]);

  // BCI disponibles en los productos: clave normalizada -> {texto original, cantidad}
  const disponibles = useMemo(() => {
    const mapa = new Map<string, SugerenciaBci>();
    productos.forEach((p: any) => {
      const clave = normBci(p.bci);
      if (!clave) return;
      const actual = mapa.get(clave);
      if (actual) actual.cantidad += 1;
      else mapa.set(clave, { bci: String(p.bci).trim(), cantidad: 1 });
    });
    return mapa;
  }, [productos]);

  // Codigos equivalentes (con una pequeña espera para no consultar en cada tecla)
  useEffect(() => {
    const q = texto.trim();
    if (normBci(q).length < 2) { setEquivalencias([]); return; }
    let cancelado = false;
    const t = setTimeout(() => {
      axios.get(`${API}/catalogo-bci/equivalencias`, { params: { q } })
        .then(r => { if (!cancelado) setEquivalencias(r.data || []); })
        .catch(() => { if (!cancelado) setEquivalencias([]); });
    }, 250);
    return () => { cancelado = true; clearTimeout(t); };
  }, [texto]);

  const sugerencias = useMemo(() => {
    const q = normBci(texto);
    const lista: (SugerenciaBci & { rango: number; clave: string })[] = [];
    disponibles.forEach((s, clave) => {
      if (!q) lista.push({ ...s, clave, rango: 0 });
      else if (clave.startsWith(q)) lista.push({ ...s, clave, rango: 0 });
      else if (clave.includes(q)) lista.push({ ...s, clave, rango: 1 });
    });
    lista.sort((a, b) => a.rango - b.rango || a.clave.localeCompare(b.clave, 'es', { numeric: true }));

    const resultado: SugerenciaBci[] = lista.map(({ bci, cantidad }) => ({ bci, cantidad }));
    const usados = new Set(lista.map(s => s.clave));
    for (const eq of equivalencias) {
      const clave = normBci(eq.bci);
      const local = disponibles.get(clave);
      if (!local || usados.has(clave)) continue; // solo BCI con productos en esta seleccion
      usados.add(clave);
      resultado.push({ bci: local.bci, cantidad: local.cantidad, via: eq.codigo });
    }
    return resultado.slice(0, 30);
  }, [texto, disponibles, equivalencias]);

  useEffect(() => { setResaltado(-1); }, [texto]);

  const elegir = (s: SugerenciaBci) => {
    onElegir(s.bci, s.via);
    setTexto('');
    setAbierto(false);
  };

  const alTeclear = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAbierto(true);
      setResaltado(i => Math.min(i + 1, sugerencias.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setResaltado(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const s = sugerencias[resaltado >= 0 ? resaltado : 0];
      if (s) elegir(s);
      else if (texto.trim()) { onElegir(texto.trim().toUpperCase()); setTexto(''); setAbierto(false); }
    } else if (e.key === 'Escape') {
      setAbierto(false);
    }
  };

  if (activo) {
    return (
      <div>
        <div className="flex items-center justify-between gap-2 bg-rojo text-white rounded-lg px-3 py-2">
          <span className="text-sm font-black">{etiquetaBci(activo)}</span>
          <button onClick={onQuitar} aria-label="Quitar filtro BCI" className="text-white/80 hover:text-white text-lg leading-none">×</button>
        </div>
        {referencia && <div className="text-xs text-gray-400 mt-1.5">Equivale a <b>{referencia}</b></div>}
      </div>
    );
  }

  if (disponibles.size === 0) {
    return <div className="text-xs text-gray-400">No hay baterías con BCI en esta categoría</div>;
  }

  return (
    <div>
      <input
        value={texto}
        onChange={e => { setTexto(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)}
        onBlur={() => setAbierto(false)}
        onKeyDown={alTeclear}
        placeholder="Ej: 24, 65, N50Z"
        autoComplete="off"
        spellCheck={false}
        aria-label="Buscar por modelo BCI"
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm uppercase placeholder:normal-case focus:outline-none focus:border-azul"
      />
      {abierto && (
        <ul className="mt-1.5 max-h-64 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50">
          {sugerencias.length === 0 ? (
            <li className="px-3 py-2 text-xs text-gray-400">
              {texto.trim() ? 'Sin coincidencias. Consúltanos por WhatsApp.' : 'Cargando...'}
            </li>
          ) : sugerencias.map((s, i) => (
            <li key={`${s.bci}-${s.via || ''}`}>
              <button
                type="button"
                onMouseDown={e => { e.preventDefault(); elegir(s); }}
                onMouseEnter={() => setResaltado(i)}
                className={`w-full text-left px-3 py-2 text-sm flex justify-between items-center gap-2 ${i === resaltado ? 'bg-azul/10' : 'hover:bg-gray-50'}`}
              >
                <span className="min-w-0">
                  <span className="font-bold text-azul">{etiquetaBci(s.bci)}</span>
                  {s.via && <span className="block text-xs text-gray-400 truncate">equivale a {s.via}</span>}
                </span>
                <span className="text-xs text-gray-400 shrink-0">{s.cantidad}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-gray-400 mt-1.5">Escribe el BCI o el código de tu batería</p>
    </div>
  );
}

export default function Catalogo() {
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const categoriaActiva = searchParams.get('categoria');
  const marcaActiva = searchParams.get('marca');
  const bciActivo = searchParams.get('bci');
  const bciReferencia = searchParams.get('bci_ref');
  const buscarParam = searchParams.get('buscar');
  const marcaAuto = searchParams.get('marca_auto');
  const modeloAuto = searchParams.get('modelo_auto');
  const bateriaParam = searchParams.get('bateria');
  const eq1Param = searchParams.get('eq1');
  const [compatibles, setCompatibles] = useState<any[]>([]);
  const [cargandoCompat, setCargandoCompat] = useState(false);
  const [modalCotizar, setModalCotizar] = useState<any>(null);
  const [formCotizar, setFormCotizar] = useState({nombre:'', telefono:'', email:''});
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [sidebarAbierto, setSidebarAbierto] = useState(false);

  useEffect(() => {
    axios.get(`${API}/categorias/`).then(r => setCategorias(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!buscarParam) { setCompatibles([]); return; }
    setCargandoCompat(true);
    axios.get(`${API}/buscador/buscar?q=${encodeURIComponent(buscarParam)}`)
      .then(r => setCompatibles(r.data))
      .catch(() => setCompatibles([]))
      .finally(() => setCargandoCompat(false));
  }, [buscarParam]);

  useEffect(() => {
    const url = categoriaActiva ? `${API}/productos/?categoria_id=${categoriaActiva}` : `${API}/productos/`;
    axios.get(url).then(r => setProductos(r.data)).catch(() => {});
  }, [categoriaActiva]);

  /** Cambia parametros de la URL conservando los demas (null = quitar). */
  const cambiarFiltros = (cambios: Record<string, string | null>) => {
    const p = new URLSearchParams(searchParams.toString());
    Object.entries(cambios).forEach(([k, v]) => (v === null ? p.delete(k) : p.set(k, v)));
    setSearchParams(p);
    setSidebarAbierto(false);
  };

  const sinBusqueda: Record<string, null> = Object.fromEntries(PARAMS_BUSQUEDA.map(k => [k, null]));

  const enviarCotizacion = async () => {
    if (!formCotizar.nombre || !formCotizar.telefono) return;
    setEnviando(true);
    try {
      await axios.post(`${API}/cotizaciones/`, {
        nombre_cliente: formCotizar.nombre,
        telefono: formCotizar.telefono,
        email: formCotizar.email,
        mensaje: `Solicitud de batería: ${modalCotizar.bateria} para ${modalCotizar.marca} ${modalCotizar.modelo}. Equivalencias: ${modalCotizar.equivalencia1 || 'N/A'}`
      });
      setEnviado(true);
      setTimeout(() => { setModalCotizar(null); setEnviado(false); setFormCotizar({nombre:'', telefono:'', email:''}); }, 3000);
    } catch {}
    setEnviando(false);
  };

  // Primero el filtro BCI; las marcas se calculan sobre ese resultado
  const productosPorBci = useMemo(
    () => (productos as any[]).filter((p: any) => !bciActivo || normBci(p.bci) === normBci(bciActivo)),
    [productos, bciActivo]
  );
  const marcas = useMemo(
    () => Array.from(new Set(productosPorBci.map((p: any) => p.marca).filter(Boolean))) as string[],
    [productosPorBci]
  );
  const productosFiltrados = productosPorBci.filter((p: any) => !marcaActiva || p.marca === marcaActiva);
  const hayFiltros = !!(categoriaActiva || marcaActiva || bciActivo);

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="bg-azul py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-white/40 text-xs tracking-widest mb-2">TIENDA</div>
          <h1 className="font-condensed text-5xl font-black text-white">Catálogo de productos</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">

        {/* Resultados búsqueda */}
        {buscarParam && (
          <div className="mb-8 bg-azul/5 border border-azul/20 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="text-azul font-black text-lg">🔍 Resultados para: "{buscarParam}"</div>
              <button onClick={() => setSearchParams({})} className="text-xs text-gray-400 hover:text-rojo transition-colors">✕ Limpiar</button>
            </div>
            {marcaAuto && bateriaParam ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                  <div className="text-xs font-bold text-azul tracking-wider mb-1">{marcaAuto}</div>
                  <div className="font-semibold text-gray-800 text-sm mb-2">{modeloAuto}</div>
                  <div className="bg-rojo text-white text-xs font-black px-3 py-1 rounded inline-block">{bateriaParam}</div>
                  {eq1Param && <div className="text-xs text-gray-400 mt-1">Equiv: {eq1Param}</div>}
                  <button onClick={() => setModalCotizar({marca: marcaAuto, modelo: modeloAuto, bateria: bateriaParam, equivalencia1: eq1Param})}
                    className="mt-3 w-full bg-rojo text-white text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-rojo-dark transition-colors">
                    📋 Solicitar esta batería
                  </button>
                </div>
              </div>
            ) : cargandoCompat ? (
              <div className="text-gray-400 text-sm">Buscando...</div>
            ) : compatibles.length === 0 ? (
              <div className="text-gray-400 text-sm">Sin resultados de compatibilidad</div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {compatibles.map((r, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                    <div className="text-xs font-bold text-azul tracking-wider mb-1">{r.marca}</div>
                    <div className="font-semibold text-gray-800 text-sm mb-2">{r.modelo}</div>
                    <div className="bg-rojo text-white text-xs font-black px-3 py-1 rounded inline-block">{r.bateria}</div>
                    {r.equivalencia1 && <div className="text-xs text-gray-400 mt-1">Equiv: {r.equivalencia1}</div>}
                    <button onClick={() => { setModalCotizar(r); setFormCotizar({nombre:'', telefono:'', email:''}); }}
                      className="mt-3 w-full bg-rojo text-white text-xs font-bold py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-rojo-dark transition-colors">
                      📋 Solicitar esta batería
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Botón filtros móvil */}
        <button onClick={() => setSidebarAbierto(!sidebarAbierto)}
          className="md:hidden mb-4 flex items-center gap-2 bg-azul text-white px-4 py-2 rounded-xl text-sm font-bold">
          ☰ Filtros {hayFiltros && <span className="bg-rojo text-white text-xs px-2 py-0.5 rounded-full">activo</span>}
        </button>

        <div className="flex gap-8">
          {/* SIDEBAR */}
          <aside className={`${sidebarAbierto ? 'block' : 'hidden'} md:block w-full md:w-56 flex-shrink-0`}>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden sticky top-20">

              {/* Categorías */}
              <div className="p-4 border-b border-gray-100">
                <div className="text-xs font-black text-gray-400 tracking-widest mb-3">CATEGORÍAS</div>
                <ul className="space-y-1">
                  <li>
                    <button onClick={() => cambiarFiltros({ ...sinBusqueda, categoria: null, marca: null })}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${!categoriaActiva ? 'bg-azul text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-azul'}`}>
                      🔋 Todos
                    </button>
                  </li>
                  {(categorias as any[]).map((c: any) => (
                    <li key={c.id}>
                      <button onClick={() => cambiarFiltros({ ...sinBusqueda, categoria: String(c.id), marca: null })}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${categoriaActiva === String(c.id) ? 'bg-azul text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-azul'}`}>
                        {c.icono_url
                          ? <img src={`https://ticobaterias.com${c.icono_url}`} alt={c.nombre} className="w-5 h-5 object-contain flex-shrink-0"/>
                          : <span>{c.icono}</span>
                        }
                        {c.nombre}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Modelos BCI */}
              <div className="p-4 border-b border-gray-100">
                <div className="text-xs font-black text-gray-400 tracking-widest mb-3">MODELOS BCI</div>
                <BuscadorBci
                  productos={productos as any[]}
                  activo={bciActivo}
                  referencia={bciReferencia}
                  onElegir={(bci, via) => cambiarFiltros({ bci, bci_ref: via || null, marca: null })}
                  onQuitar={() => cambiarFiltros({ bci: null, bci_ref: null })}
                />
              </div>

              {/* Marcas */}
              {marcas.length > 0 && (
                <div className="p-4">
                  <div className="text-xs font-black text-gray-400 tracking-widest mb-3">MARCAS DE BATERÍAS</div>
                  <ul className="space-y-1">
                    <li>
                      <button onClick={() => cambiarFiltros({ marca: null })}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${!marcaActiva ? 'bg-rojo text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-rojo'}`}>
                        Todas
                      </button>
                    </li>
                    {marcas.map(m => (
                      <li key={m}>
                        <button onClick={() => cambiarFiltros({ marca: m })}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${marcaActiva === m ? 'bg-rojo text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-rojo'}`}>
                          {m}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </aside>

          {/* PRODUCTOS */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-5">
              <div className="text-sm text-gray-500">
                {productosFiltrados.length} productos
                {bciActivo && <> con <b className="text-azul">{etiquetaBci(bciActivo)}</b></>}
              </div>
              {hayFiltros && (
                <button onClick={() => setSearchParams({})} className="text-xs text-rojo hover:underline font-medium">
                  ✕ Limpiar filtros
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {productosFiltrados.map((p: any) => (
                <div key={p.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-azul hover:shadow-lg hover:-translate-y-1 transition-all group">
                  <div className="bg-gray-50 h-36 flex items-center justify-center border-b border-gray-100 relative">
                    {p.imagen_url
                      ? <img src={p.imagen_url} alt={p.nombre} className="h-full w-full object-cover"/>
                      : <span className="text-5xl">🔋</span>
                    }
                    {p.precio_oferta && <span className="absolute top-2 left-2 fire-badge text-lg">🔥</span>}
                  </div>
                  <div className="p-4">
                    <div className="font-condensed text-base font-black text-azul leading-tight mb-1">{p.nombre}</div>
                    {p.bci && (
                      <button onClick={() => cambiarFiltros({ bci: p.bci, bci_ref: null, marca: null })}
                        title="Ver todas las baterías con este BCI"
                        className="inline-block bg-azul/10 text-azul text-xs font-black px-2 py-0.5 rounded mb-2 hover:bg-azul hover:text-white transition-colors">
                        {etiquetaBci(p.bci)}
                      </button>
                    )}
                    <DescripcionExpandible texto={p.descripcion} />
                    {p.compatibilidad && (
                      <div className="text-xs text-azul/70 mb-2 line-clamp-2">
                        🚗 {p.compatibilidad}
                      </div>
                    )}
                    {p.precio_oferta ? (
                      <div className="mb-3">
                        <div className="text-azul text-sm font-bold line-through opacity-60">₡{p.precio?.toLocaleString()}</div>
                        <div className="font-condensed text-3xl font-black text-rojo price-fire">₡{p.precio_oferta?.toLocaleString()}</div>
                      </div>
                    ) : (
                      <div className="font-condensed text-xl font-black text-rojo mb-3">₡{p.precio?.toLocaleString()}</div>
                    )}
                    <a href={`https://wa.me/50685076464?text=Me interesa: ${p.nombre}`} target="_blank" rel="noreferrer"
                      className="w-full bg-azul text-white py-2 rounded font-bold text-xs flex items-center justify-center gap-1 hover:bg-azul-dark transition-colors">
                      💬 Cotizar
                    </a>
                  </div>
                </div>
              ))}
              {productosFiltrados.length === 0 && (
                <div className="col-span-3 text-center py-20 text-gray-400">
                  <div className="text-5xl mb-4">🔋</div>
                  {bciActivo ? (
                    <>
                      <p>No encontramos baterías {etiquetaBci(bciActivo)} en esta selección.</p>
                      <a href={`https://wa.me/50685076464?text=${encodeURIComponent(`Hola, busco una batería ${etiquetaBci(bciActivo)}`)}`}
                        target="_blank" rel="noreferrer"
                        className="inline-block mt-4 bg-[#25D366] text-white px-5 py-2 rounded-full text-sm font-bold">
                        💬 Consultar por WhatsApp
                      </a>
                    </>
                  ) : (
                    <p>No hay productos en esta categoría</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Modal Cotización */}
      {modalCotizar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-azul">Solicitar batería</h2>
              <p className="text-sm text-gray-500 mt-1">Te contactamos a la brevedad</p>
            </div>
            <div className="p-6 space-y-4">
              {/* Info pre-llenada */}
              <div className="bg-azul/5 border border-azul/20 rounded-xl p-4">
                <div className="text-xs font-bold text-azul tracking-wider mb-1">{modalCotizar.marca} — {modalCotizar.modelo}</div>
                <div className="flex items-center gap-2">
                  <span className="bg-rojo text-white text-xs font-black px-3 py-1 rounded">{modalCotizar.bateria}</span>
                  {modalCotizar.equivalencia1 && <span className="text-xs text-gray-400">Equiv: {modalCotizar.equivalencia1}</span>}
                </div>
              </div>
              {enviado ? (
                <div className="text-center py-6">
                  <div className="text-4xl mb-3">✅</div>
                  <div className="font-bold text-azul">¡Solicitud enviada!</div>
                  <div className="text-sm text-gray-500 mt-1">Te contactaremos pronto</div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1.5">NOMBRE *</label>
                    <input value={formCotizar.nombre} onChange={e => setFormCotizar({...formCotizar, nombre: e.target.value})}
                      placeholder="Tu nombre completo"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-azul"/>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1.5">TELÉFONO *</label>
                    <input value={formCotizar.telefono} onChange={e => setFormCotizar({...formCotizar, telefono: e.target.value})}
                      placeholder="8888-8888"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-azul"/>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 tracking-wider block mb-1.5">EMAIL</label>
                    <input value={formCotizar.email} onChange={e => setFormCotizar({...formCotizar, email: e.target.value})}
                      placeholder="correo@ejemplo.com" type="email"
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-azul"/>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button onClick={() => setModalCotizar(null)}
                      className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
                      Cancelar
                    </button>
                    <button onClick={enviarCotizacion} disabled={enviando || !formCotizar.nombre || !formCotizar.telefono}
                      className="flex-1 bg-azul text-white py-2.5 rounded-xl font-bold text-sm hover:bg-azul-dark transition-colors disabled:opacity-50">
                      {enviando ? 'Enviando...' : '📋 Enviar solicitud'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
